import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CountdownRing } from "./CountdownRing";
import { invoke } from "./tauri-web";
import { listen } from "./tauri-web";
import { supabase } from "./supabase";
import { store, type FocusSession, type Settings, type WeeklySignals, type HeadlineFact, type FactTone, type PlannedSession, type Goal } from "./store";
import { FocusPlanSection } from "./FocusPlanFlow";
import GoalsView from "./GoalsView";
import { weeklySummary, openWebLogin, BrainError, type BrainErrorCode } from "./brain";
import Chat from "./chat/Chat";
import { OrioraBadge } from "./OrioraBadge";
import { BackupMenu } from "./BackupMenu";
import { platform } from "./tauri-web";
import { AccountMenu } from "./AccountMenu";
import { QuietMenu } from "./QuietMenu";
import { WalletTopUp } from "./iap";

// AI-data consent (App Store Guideline 5.1.1(i)/5.1.2): one-time disclosure shown after sign-in,
// before any AI feature is used. Persisted in localStorage so it's shown only once.
const AI_CONSENT_KEY = "oriora.ai_consent.v1";

function AiConsent({ onAccept }: { onAccept: () => void }) {
  const openPrivacy = () => { void invoke("open_external", { url: "https://orioralabs.com/about/privacy" }); };
  return (
    <main style={{ height: "100dvh", display: "flex", flexDirection: "column", overflowY: "auto", WebkitOverflowScrolling: "touch" as any, boxSizing: "border-box", padding: "max(1.25rem, env(safe-area-inset-top)) 1.25rem max(1.25rem, env(safe-area-inset-bottom))" }}>
      <div className="card" style={{ width: "100%", maxWidth: 380, padding: "2rem", margin: "auto" }}>
        <div style={{ fontWeight: 700, fontSize: "var(--fs-sm)", textAlign: "center" }}>Solo Focus Log</div>
        <h1 style={{ fontSize: "1.4rem", margin: "0.5rem 0 0.85rem", textAlign: "center" }}>How Oriora uses AI</h1>
        <p className="muted" style={{ fontSize: "var(--fs-xs)", lineHeight: 1.6, marginBottom: "0.85rem" }}>
          When you use the AI weekly summary, Solo Focus Log sends the focus-session data for that
          summary to third-party AI providers (Anthropic, OpenAI, Google, and others) to generate it.
        </p>
        <p className="muted" style={{ fontSize: "var(--fs-xs)", lineHeight: 1.6, marginBottom: "1.25rem" }}>
          Your data stays yours — we don't retain it: data passes through Oriora for each AI action and isn't kept by us. You choose where it lives — this device, and optionally your own GitHub or Google Drive. By
          continuing, you agree to this.
        </p>
        <button className="btn" style={{ width: "100%", marginBottom: "0.6rem" }} onClick={onAccept}>I Agree</button>
        <button className="btn btn-ghost" style={{ width: "100%" }} onClick={openPrivacy}>Privacy Policy</button>
      </div>
    </main>
  );
}

const DURATIONS = [25, 50, 90] as const;
const todayISO = () => new Date().toISOString();
const fmtDate = (iso: string) => new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
const fmtTime = (iso: string) => new Date(iso).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
const fmtClock = (iso: string) => new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

export default function App() {
  const [mounted, setMounted] = useState(false);
  const [authed, setAuthed] = useState(false);
  const [email, setEmail] = useState<string | null>(null);

  useEffect(() => {
    // onAuthStateChange fires INITIAL_SESSION after async filesystem storage loads —
    // avoids the cold-launch race where getSession() returned null before storage was ready.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, session) => {
      setAuthed(session !== null);
      setEmail(session?.user?.email ?? null);
      setMounted(true);
    });
    const timeout = setTimeout(() => setMounted(true), 3000);
    return () => { clearTimeout(timeout); subscription.unsubscribe(); };
  }, []);

  // Deep-link auth: web login returns solo-focus-log://auth#access_token=…&refresh_token=…
  useEffect(() => {
    let unlisten: (() => void) | null = null;
    listen<string>("auth:deep-link", async (e) => {
      try {
        const u = new URL(e.payload);
        const frag = new URLSearchParams(u.hash.replace(/^#/, ""));
        const access_token = frag.get("access_token");
        const refresh_token = frag.get("refresh_token");
        if (access_token && refresh_token) await supabase.auth.setSession({ access_token, refresh_token });
      } catch (err) {
        console.error("deep-link auth failed", err);
      }
    }).then((u) => { unlisten = u; });
    return () => { if (unlisten) unlisten(); };
  }, []);

  const [consented, setConsented] = useState(() => {
    try { return localStorage.getItem(AI_CONSENT_KEY) === "1"; } catch { return false; }
  });
  const acceptConsent = () => {
    try { localStorage.setItem(AI_CONSENT_KEY, "1"); } catch { /* ignore — still consented this session */ }
    setConsented(true);
  };

  if (!mounted) return <div style={{ height: "100%", display: "grid", placeItems: "center" }} className="muted">Loading…</div>;
  if (!authed) return <Login />;
  if (!consented) return <AiConsent onAccept={acceptConsent} />;
  return <Dashboard email={email} />;
}

const APP_SCHEME = "solo-focus-log";
// iOS sign-in: present orioralabs.com/login inside ASWebAuthenticationSession (in-app Safari, Apple Guideline 4);
// the solo-focus-log://auth#tokens redirect is captured by the session and returned here.
async function signInIos(): Promise<void> {
  const { callbackUrl } = await invoke<{ callbackUrl: string }>("plugin:web-auth|authenticate", {
    payload: { url: `https://orioralabs.com/login?return=${APP_SCHEME}`, callbackScheme: APP_SCHEME },
  });
  const frag = new URLSearchParams(new URL(callbackUrl).hash.replace(/^#/, ""));
  const access_token = frag.get("access_token");
  const refresh_token = frag.get("refresh_token");
  if (access_token && refresh_token) await supabase.auth.setSession({ access_token, refresh_token });
}

// §2 (#61, Guideline 4) — macOS sign-in in the app's OWN WebKit window (open_login_window, ipc.rs).
// NOT ASWebAuthenticationSession: on a Mac whose default browser is Chrome, macOS hands that session
// to the browser — Apple rejected three sibling macOS builds for it (Guideline 4, 2026-09-08: "the user
// is taken to the default web browser to sign in"), reproduced live 2026-09-11 (ephemeral AND non-ephemeral).
// The in-app window (`https://orioralabs.com/login?return=solo-focus-log`) hands the session back via
// `auth:deep-link` (App.tsx listener) and closes itself — proven end-to-end on Analyst 2026-09-12:
// Turnstile solved in-window, email code accepted, app signed in; Apple approved all three 2026-09-21.
async function signInMac(): Promise<void> {
  await invoke("open_login_window");
}

function Login() {
  const [busy, setBusy] = useState(false);
  // 2026-09-02: a failed sign-in used to vanish into `finally` — Apple saw "Sign in with Oriora unresponsive".
  // Whatever goes wrong is now shown under the button.
  const [err, setErr] = useState<string | null>(null);
  return (
    <main style={{ height: "100%", display: "grid", placeItems: "center", padding: "1.5rem" }}>
      {/* Fill-then-cap. A bare `width: 380` cannot shrink — `.card` carries no max-width — so on a
          375px phone this card measured 24..404 and lost 29px off the right edge. The consent gate
          above already does it this way; this one was missed. */}
      <div className="card" style={{ width: "100%", maxWidth: 380, padding: "2.25rem", textAlign: "center" }}>
        {/* Brand mark — echoes the app icon's focus-dial (ring + single hand) */}
        <svg width="56" height="56" viewBox="0 0 56 56" style={{ margin: "0 auto 1.25rem", display: "block" }} aria-hidden>
          <circle cx="28" cy="28" r="22" fill="none" stroke="var(--accent)" strokeWidth="4" />
          <line x1="28" y1="28" x2="28" y2="11" stroke="var(--accent)" strokeWidth="4" strokeLinecap="round" />
          <circle cx="28" cy="28" r="3.5" fill="var(--accent)" />
        </svg>
        <div style={{ color: "var(--accent)", fontWeight: 700, fontSize: "var(--fs-sm)" }}>Oriora Focus</div>
        <h1 style={{ fontSize: "var(--fs-display)", margin: "0.5rem 0 0.4rem", fontWeight: 700 }}>Track your deep work</h1>
        <p className="muted" style={{ fontSize: "var(--fs-sm)", lineHeight: 1.55, marginBottom: "1.5rem", color: "var(--ink-2)" }}>
          {/* §7b — "your focus sessions stay on this device" was FALSE: this app mounts <CloudBackup>,
              which relays the whole session store through Oriora to the user's own cloud the moment they
              tap Back up. The weekly-summary half was already honest; the qualifier is what was missing.
              Wording follows the approved Study Companion / Lockbox form — the claim is about RETENTION,
              never visibility. */}
          Sign in with your Oriora account — your browser opens, then returns you here. Your focus
          sessions stay on this device unless you back them up; only the weekly summary text passes
          through Oriora, which doesn&apos;t retain it.
        </p>
        <button className="btn btn-primary" style={{ width: "100%", height: 48 }} disabled={busy}
          onClick={async () => { setBusy(true); setErr(null); try { const p = platform(); if (p === "ios") await signInIos(); else if (p === "macos") await signInMac(); else if (p === "web") window.location.href = "/login?next=/solo-focus-log/"; else await openWebLogin(); } catch (e) { setErr(String((e as any)?.message ?? e)); } finally { setBusy(false); } }}>
          {busy ? "Opening browser…" : "Sign in with Oriora"}
        </button>
        {err && <div role="alert" style={{ marginTop: 10, fontSize: "var(--fs-xs)", color: "#dc2626" }}>Sign-in could not start: {err}</div>}
        <p className="muted" style={{ fontSize: "var(--fs-xs)", lineHeight: 1.5, marginTop: "1rem", marginBottom: "0.6rem", color: "var(--ink-3)" }}>
          No separate password — Oriora handles sign-in. The optional weekly summary uses third-party
          AI providers (Anthropic, OpenAI, Google, and others) via Oriora.
        </p>
        <button className="btn btn-ghost" style={{ fontSize: "var(--fs-xs)", padding: "0.2rem 0.4rem" }}
          onClick={() => void invoke("open_external", { url: "https://orioralabs.com/about/privacy" })}>
          Privacy Policy →
        </button>
      </div>
    </main>
  );
}

type Tab = "timer" | "history" | "goals" | "settings";

function Dashboard({ email }: { email: string | null }) {
  // No tab state — the 4-tab [timer|history|goals|settings] row (the shared 14-app skeleton) is gone.
  // Timer + record + goals now live on one scrolling home; Settings is a demoted overlay.
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [sessions, setSessions] = useState<FocusSession[]>([]);
  const [settings, setSettings] = useState<Settings>({ default_duration_min: 25 });
  // Offline awareness — local sessions still save; only the AI summary pauses.
  const [offline, setOffline] = useState(typeof navigator !== "undefined" && navigator.onLine === false);
  // Chat panel (ADDITIVE): a natural-language front door over the SAME capabilities — does not change
  // any existing screen. onChanged → reload() refreshes sessions/settings after a chat-driven mutation.
  const [chatOpen, setChatOpen] = useState(false);
  // Focus Plan (AI-deepening feature) — when a planned slot is started, its id rides with the
  // Timer so completion can mark it done (mirrors Workout Tracker's pendingSessionId).
  const [pendingPlan, setPendingPlan] = useState<PlannedSession | null>(null);
  const [planRefresh, setPlanRefresh] = useState(0);
  // Chat-driven "draft me a plan" / "repeat my last session" — mirrors Workout Tracker's
  // programDraftTrigger / repeatLastWorkout wiring.
  const [planDraftTrigger, setPlanDraftTrigger] = useState(0);
  const [repeatDuration, setRepeatDuration] = useState<number | null>(null);
  // Chat-driven timer start ("start a 25 minute session") — the assistant could log/plan/summarize
  // but NOT run the app's one core action; it told users "just tap Start" (found live 2026-08-27).
  const [chatStart, setChatStart] = useState<{ min: number; nonce: number } | null>(null);
  // Bumped alongside the general reload() so a chat-driven goal change (create_goal) shows up in
  // GoalsView even while that view is already on the home surface (chat is an overlay).
  const [goalsRefresh, setGoalsRefresh] = useState(0);
  // (The old most-neglected goal-nudge banner was removed in the a3 restructure — goals now lead the
  // start block as the commitment and have their own home section, so the orphan strip is redundant.)

  const startFocusFromChat = useCallback((min: number): boolean => {
    const m = Math.max(1, Math.min(180, Math.round(min)));
    setChatStart((c) => ({ min: m, nonce: (c?.nonce ?? 0) + 1 }));
    setChatOpen(false); // let them SEE the clock running
    return true;
  }, []);

  const repeatLastSession = useCallback(async (): Promise<FocusSession | null> => {
    const all = await store.listSessions(50000);
    if (all.length === 0) return null;
    const last = [...all].sort((a, b) => (a.started_at < b.started_at ? 1 : -1))[0];
    setRepeatDuration(last.duration_min);
    return last;
  }, []);

  const reload = useCallback(async () => {
    const [s, cfg] = await Promise.all([store.listSessions(), store.getSettings()]);
    setSessions(s); setSettings(cfg);
  }, []);

  useEffect(() => { void reload(); }, [reload]);

  useEffect(() => {
    const goOnline = () => setOffline(false);
    const goOffline = () => setOffline(true);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, []);

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Padding lives in .app-header (CSS), not inline, so the narrow-phone media query can win —
          an inline style would need !important to override. Measured at 375px: brand + 3 tabs +
          avatar came to 398px inside a 327px content box, pushing the avatar to 358..392, i.e.
          half of the account button off the right edge on every screen. */}
      {/* 🔴 IDENTITY (2026-08-30): there is NO wordmark header row. The COMMITMENT LINE
          ("This block is for…") is the header — a focus tool opens on what you are about to do,
          not on the app's own name, and the brief's material for this app is ONE uninterrupted
          surface. The backup / settings / account controls still have to be reachable, so they
          collapse into a single recessive control riding on that block (QuietMenu). Do not
          reintroduce a wordmark strip, and do not split the control back into three buttons. */}

      {/* ONE scrolling home: the commitment-led start block → the AI Focus Plan → the tracked record
          → goals. This is the Forest/Rize move (the record IS the home) and the only shape that makes
          the home actually deliver "Track your deep work". */}
      <main style={{ flex: 1, overflowY: "auto", maxWidth: 680, width: "100%", margin: "0 auto", padding: "1.5rem" }}>
        {offline && (
          <div role="status" className="row" style={{ gap: "0.45rem", fontSize: "var(--fs-sm)", color: "var(--amber)", background: "color-mix(in srgb, var(--amber) 10%, transparent)", border: "1px solid color-mix(in srgb, var(--amber) 28%, transparent)", borderRadius: "var(--radius-sm)", padding: "0.5rem 0.85rem", marginBottom: "1.25rem" }}>
            Offline — sessions still save on this device; the weekly AI summary resumes when you reconnect.
          </div>
        )}

        <Timer
          onAsk={() => setChatOpen(true)}
          chrome={
            <QuietMenu
              onSettings={() => setSettingsOpen(true)}
              backup={
                <BackupMenu
                  appName="Focus Log" fileSlug="focus-log" appScheme="solo-focus-log"
                  hasData={sessions.length > 0} onRestored={reload}
                />
              }
              account={<AccountMenu email={email} appSlug="solo-focus-log" appName="Oriora Focus" />}
            />
          }
          defaultDuration={pendingPlan?.duration_min ?? repeatDuration ?? settings.default_duration_min}
          pendingPlan={pendingPlan}
          onStarted={() => setRepeatDuration(null)}
          chatStart={chatStart}
          onSaved={() => { void reload(); setPlanRefresh((n) => n + 1); }}
          onSessionSaved={async (sessionId) => {
            if (pendingPlan) { await store.completePlannedSession(pendingPlan.id, sessionId); setPendingPlan(null); }
          }}
        />

        {/* The AI-drafted multi-session plan — the one capability no studied peer has — promoted to
            the spine, directly under the commitment it's drafted from. */}
        <div style={{ marginTop: "2rem" }}>
          <FocusPlanSection
            weeklySignals={() => store.weeklySignals(56)}
            refreshSignal={planRefresh}
            triggerDraft={planDraftTrigger}
            onPlanChanged={() => setPlanRefresh((n) => n + 1)}
            onStart={(session) => setPendingPlan(session)}
          />
        </div>

        {/* The tracked record, on the home surface (streak · when-you-focus grid · weekly AI insight ·
            recent sessions with reflections). */}
        <div style={{ marginTop: "2rem" }}>
          <History sessions={sessions} offline={offline} onChange={reload} />
        </div>

        {/* Goals — the other capability peers lack, promoted from a buried tab to the home spine. */}
        <div style={{ marginTop: "2.25rem" }}>
          <GoalsView refreshSignal={goalsRefresh} />
        </div>
      </main>

      {settingsOpen && (
        <div className="modal-overlay" onClick={() => setSettingsOpen(false)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()} style={{ width: "100%", maxWidth: 420, maxHeight: "85vh", overflowY: "auto" }}>
            <SettingsView email={email} settings={settings} onChange={reload} />
            <button className="btn" style={{ width: "100%", marginTop: "0.5rem" }} onClick={() => setSettingsOpen(false)}>Close</button>
          </div>
        </div>
      )}

      {/* Chat panel — a natural-language way to use the app by typing. Purely additive: the launcher
          + slide-over live on top; no existing screen is altered. A chat-driven change reloads the view. */}
      {/* 🔴 IDENTITY (2026-08-30): the floating coral "Chat" pill that used to launch this was
          DELETED — same pill, same corner, same label in ~15 sibling apps, and this app's brief
          says NO floating pill at all. The assistant is NOT removed and must never be: its entry
          is the .sfl-ask line inside the start block, which calls this same setChatOpen. If a
          future change moves it, REPLACE it — never drop it. */}
      <aside className={`sfc-sheet ${chatOpen ? "open" : ""}`} aria-hidden={!chatOpen}>
        <div className="sfc-head">
          <span className="row" style={{ gap: "0.35rem" }}><SparkleDot /> Assistant — log, search, summarize</span>
          <button className="link" onClick={() => setChatOpen(false)}>Close ↓</button>
        </div>
        <Chat
          onChanged={() => { void reload(); setGoalsRefresh((n) => n + 1); }}
          onOpenPlanFlow={() => { setChatOpen(false); setPlanDraftTrigger((n) => n + 1); }}
          onRepeatLastSession={repeatLastSession}
          onStartFocus={startFocusFromChat}
        />
      </aside>
    </div>
  );
}

// Tiny inline sparkle (this app uses inline SVGs, not an icon library).
function SparkleDot() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden style={{ display: "block", flexShrink: 0 }}>
      <path d="M12 3l1.8 4.9L18.5 9.7 13.8 11.5 12 16.5 10.2 11.5 5.5 9.7 10.2 7.9 12 3z" fill="currentColor" />
    </svg>
  );
}

// A completion alert is baseline category table-stakes (every competitor researched — Forest,
// Focus Keeper, Be Focused — chimes when a session ends; this app had none). Synthesized via Web
// Audio, not a bundled sound file — a calm two-note chime matching the "restrained, not gamified"
// brand, not a jarring alarm. Silently no-ops if audio is unavailable (never breaks the timer).
function playChime() {
  try {
    const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    const ctx = new Ctx();
    const notes = [660, 880];
    notes.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      const start = ctx.currentTime + i * 0.16;
      gain.gain.setValueAtTime(0, start);
      gain.gain.linearRampToValueAtTime(0.18, start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, start + 0.5);
      osc.connect(gain).connect(ctx.destination);
      osc.start(start);
      osc.stop(start + 0.55);
    });
    setTimeout(() => void ctx.close(), 1200);
  } catch { /* audio unavailable — the visual completion state still shows */ }
}

// ── Timer ─────────────────────────────────────────────────────────────────────
function Timer({ defaultDuration, onSaved, pendingPlan, onSessionSaved, onStarted, chatStart, onAsk, chrome }: {
  defaultDuration: number;
  onSaved: () => void;
  pendingPlan?: PlannedSession | null;
  onSessionSaved?: (sessionId: string) => void;
  onStarted?: () => void;
  chatStart?: { min: number; nonce: number } | null;
  onAsk: () => void;
  chrome: React.ReactNode;
}) {
  const [selected, setSelected] = useState<number>(defaultDuration);
  const [running, setRunning] = useState(false);
  const [endsAt, setEndsAt] = useState<string | null>(null);
  const [startedAt, setStartedAt] = useState<string | null>(null);
  const [duration, setDuration] = useState(defaultDuration);
  const [done, setDone] = useState(false);
  const [showReflection, setShowReflection] = useState(false);
  const [reflection, setReflection] = useState("");
  const [saving, setSaving] = useState(false);
  const [, setTick] = useState(0);
  const completionTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [goals, setGoals] = useState<Goal[]>([]);
  const [goalId, setGoalId] = useState<string>("");
  useEffect(() => { void store.listGoals().then(setGoals); }, []);

  useEffect(() => { setSelected(defaultDuration); }, [defaultDuration]);
  // A chat "start a session" lands here: set the duration and start in one go. A session already
  // running wins — chat never stomps a live block.
  const chatNonce = chatStart?.nonce ?? 0;
  useEffect(() => {
    if (!chatStart || chatNonce === 0) return;
    if (running) return;
    setSelected(chatStart.min);
    start(chatStart.min);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatNonce]);

  // Scheduled-completion framing (spec §11.2): a single setTimeout that fires at end —
  // NOT a 1s countdown. Display values are computed from wall-clock on render.
  useEffect(() => {
    if (!running || !endsAt) return;
    const ms = new Date(endsAt).getTime() - Date.now();
    if (ms <= 0) { setDone(true); setShowReflection(true); return; }
    completionTimer.current = setTimeout(() => { setDone(true); setShowReflection(true); playChime(); }, ms);
    // Refresh the "min remaining" caption once a minute (perceptual only).
    const minuteTick = setInterval(() => setTick((t) => t + 1), 30000);
    return () => {
      if (completionTimer.current) { clearTimeout(completionTimer.current); completionTimer.current = null; }
      clearInterval(minuteTick);
    };
  }, [running, endsAt]);

  const remaining = useMemo(() => {
    if (!endsAt) return { clock: "--:--", label: "", done: false };
    const ms = new Date(endsAt).getTime() - Date.now();
    const clock = fmtClock(endsAt);
    if (ms <= 0) return { clock, label: "", done: true };
    const min = Math.ceil(ms / 60000);
    return { clock, label: min < 1 ? "Less than 1 min remaining" : `${min} min remaining`, done: false };
  }, [endsAt, done]);

  function start(overrideMin?: number) {
    const mins = overrideMin ?? selected;
    const now = new Date();
    setStartedAt(now.toISOString());
    setEndsAt(new Date(now.getTime() + mins * 60_000).toISOString());
    setDuration(mins);
    setRunning(true);
    setDone(false);
    onStarted?.();
  }

  function stop() {
    if (completionTimer.current) { clearTimeout(completionTimer.current); completionTimer.current = null; }
    setRunning(false); setEndsAt(null); setStartedAt(null); setDone(false);
  }

  async function saveSession(completed: boolean) {
    setSaving(true);
    try {
      const saved = await store.createSession({
        duration_min: duration,
        started_at: startedAt ?? todayISO(),
        completed_at: completed ? todayISO() : null,
        reflection: reflection.trim() || null,
        goal_id: goalId || null,
      });
      if (pendingPlan) await onSessionSaved?.(saved.id);
      onSaved();
    } finally {
      setSaving(false); setShowReflection(false); setReflection(""); stop();
    }
  }

  function discard() { setShowReflection(false); setReflection(""); stop(); }

  return (
    <div>
      {/* Idle → the commitment leads: what this block is FOR (the goal), then the duration demoted
          inside the same card. Replaces the old generic "Focus Session" heading + bare chip picker. */}
      {!running ? (
        <div className="card" style={{ padding: "1.5rem 1.5rem 1.75rem", position: "relative" }}>
          {/* the app's ONE chrome control rides on the commitment block — see the IDENTITY note
              where the wordmark header row used to be */}
          <div style={{ position: "absolute", top: "0.9rem", right: "0.9rem", zIndex: 3 }}>{chrome}</div>
          <div className="display-eyebrow">This block is for</div>
          {goals.length > 0 ? (
            <select value={goalId} onChange={(e) => setGoalId(e.target.value)} style={{ marginTop: "0.65rem", fontFamily: "var(--font-display)", fontSize: "1.15rem", padding: "0.6rem 0.7rem" }} aria-label="What this focus block is for">
              <option value="">Open focus — no goal</option>
              {goals.map((g) => <option key={g.id} value={g.id}>{g.title}</option>)}
            </select>
          ) : (
            <div className="muted" style={{ marginTop: "0.5rem", fontSize: "var(--fs-sm)", lineHeight: 1.5 }}>Name what your deep work is for by adding a goal (further down) — or just start an open block.</div>
          )}
          <div style={{ display: "grid", placeItems: "center", gap: "1.25rem", marginTop: "1.5rem" }}>
            <div className="micro">Duration</div>
            <div className="row" style={{ gap: "0.75rem" }}>
              {DURATIONS.map((d) => (
                <button key={d} className={`chip ${selected === d ? "active" : ""}`} onClick={() => setSelected(d)}>{d}m</button>
              ))}
            </div>
            <div className="clock"><span className="clock-text">{String(selected).padStart(2, "0")}:00</span></div>
            <button className="btn btn-primary" style={{ width: "100%", maxWidth: 360, height: 52 }} onClick={() => start()}>Start Focus</button>
          </div>

          {/* 🔴 THE ASSISTANT ENTRY — inside the block, per this app's brief ("no floating pill at
              all — the assistant enters from the block card itself"). onAsk opens the same Chat
              sheet: the AI is moved, never removed. */}
          <button type="button" className="sfl-ask" onClick={onAsk} aria-label="Open chat assistant">
            <span className="sfl-ask-mark" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none">
                <circle cx="12" cy="12" r="8.4" stroke="currentColor" strokeWidth="1.6" />
                <path d="M12 7.4V12l3 1.9" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
              </svg>
            </span>
            <span className="sfl-ask-txt">
              <span className="sfl-ask-lbl">Ask</span>
              <span className="sfl-ask-sub">log · search · summarize</span>
            </span>
          </button>
        </div>
      ) : (
        <div style={{ display: "grid", placeItems: "center", gap: "1.5rem", position: "relative" }}>
          <div className={`clock active ${remaining.done ? "just-completed" : ""}`}>
            <CountdownRing running={!remaining.done} durationSec={duration * 60} />
            <div>
              <span className={`clock-text ${remaining.done ? "done" : ""}`}>{remaining.done ? "Done!" : remaining.clock}</span>
              {!remaining.done && <div className="clock-sub">Session ends · {remaining.clock}</div>}
            </div>
          </div>
          {!remaining.done && remaining.label && <div style={{ color: "var(--ink-2)", fontSize: "var(--fs-lg)", fontWeight: 500 }}>{remaining.label}</div>}
          {!remaining.done && (
            <button className="btn btn-danger" style={{ width: "100%", maxWidth: 360, height: 52 }} onClick={stop}>Stop</button>
          )}
          {/* the one chrome control and the assistant stay reachable while a block runs */}
          <div style={{ position: "absolute", top: 0, right: 0, zIndex: 3 }}>{chrome}</div>
          <button type="button" className="sfl-ask" style={{ maxWidth: 360 }} onClick={onAsk} aria-label="Open chat assistant">
            <span className="sfl-ask-mark" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none">
                <circle cx="12" cy="12" r="8.4" stroke="currentColor" strokeWidth="1.6" />
                <path d="M12 7.4V12l3 1.9" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
              </svg>
            </span>
            <span className="sfl-ask-txt">
              <span className="sfl-ask-lbl">Ask</span>
              <span className="sfl-ask-sub">log · search · summarize</span>
            </span>
          </button>
        </div>
      )}

      {showReflection && (
        <div className="modal-overlay">
          <div className="modal-card">
            <h2 style={{ fontSize: "var(--fs-heading)", fontWeight: 700, margin: "0 0 0.5rem" }}>Session done</h2>
            <p style={{ color: "var(--ink-2)", fontSize: "var(--fs-lg)", margin: "0 0 1.25rem" }}>You focused for {duration} minutes.</p>
            <div className="micro" style={{ marginBottom: "0.5rem" }}>Add a reflection</div>
            <textarea rows={4} maxLength={500} placeholder="Notes from this session" value={reflection} onChange={(e) => setReflection(e.target.value)} style={{ resize: "vertical", marginBottom: "1.25rem" }} />
            {saving ? (
              <div className="muted" style={{ textAlign: "center" }}>Saving…</div>
            ) : (
              <>
                <button className="btn btn-primary" style={{ width: "100%", height: 52, marginBottom: "0.6rem" }} onClick={() => saveSession(true)}>Save</button>
                <button className="btn btn-ghost" style={{ width: "100%", height: 44 }} onClick={discard}>Discard</button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── History (with the AI weekly summary feature) ────────────────────────────────
function History({ sessions, offline, onChange }: { sessions: FocusSession[]; offline: boolean; onChange: () => void }) {
  return (
    <div>
      <div className="row" style={{ justifyContent: "space-between", alignItems: "center", marginBottom: "1rem", flexWrap: "wrap", gap: "0.4rem" }}>
        <h1 style={{ fontSize: "var(--fs-heading)", fontWeight: 700, margin: 0 }}>Your record</h1>
      </div>
      <WeeklyInsight offline={offline} />
      <StreakStrip sessions={sessions} />
      {sessions.length === 0 ? (
        <div className="card" style={{ padding: "2.5rem", textAlign: "center" }}>
          <div style={{ width: 64, height: 64, borderRadius: "50%", border: "1.5px solid var(--ink-3)", margin: "0 auto 1rem" }} />
          <div style={{ fontWeight: 700, fontSize: "1.25rem", marginBottom: "0.4rem" }}>No sessions yet</div>
          <div className="muted" style={{ color: "var(--ink-2)" }}>Start your first focus block above — it'll show up here.</div>
        </div>
      ) : (
        <>
          <FocusGrid sessions={sessions} />
          {sessions.map((s) => <SessionCard key={s.id} s={s} onChange={onChange} />)}
        </>
      )}
    </div>
  );
}

// A real streak (consecutive days with a session), not gamification for its own sake — Forest and
// Habi both validate this as a genuinely motivating mechanic for this category (research pass
// 2026-07-31). A growth metaphor distinct from Workout's ring and this app's own ambient wash:
// seven nodes across the week, the streak visibly BUILDING left→right as a connecting line, not a
// static heatmap. Today doesn't break the streak until the day is actually over.
function StreakStrip({ sessions }: { sessions: FocusSession[] }) {
  const now = new Date();
  const dayKey = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const days: { key: string; label: string; date: Date }[] = [];
  for (let i = 6; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() - i);
    days.push({ key: dayKey(d), label: d.toLocaleDateString(undefined, { weekday: "narrow" }), date: d });
  }
  const activeDays = new Set<string>();
  for (const s of sessions) {
    const dt = new Date(s.started_at);
    if (Number.isNaN(dt.getTime())) continue;
    activeDays.add(dayKey(dt));
  }
  if (activeDays.size === 0) return null;

  let streak = 0;
  {
    const cursor = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    if (!activeDays.has(dayKey(cursor))) cursor.setDate(cursor.getDate() - 1);
    for (;;) {
      if (!activeDays.has(dayKey(cursor))) break;
      streak++;
      cursor.setDate(cursor.getDate() - 1);
    }
  }
  const activeIdx = days.reduce<number[]>((acc, d, i) => (activeDays.has(d.key) ? [...acc, i] : acc), []);
  const fill = activeIdx.length <= 1 ? 0 : (activeIdx[activeIdx.length - 1] - activeIdx[0]) / 6;

  return (
    <div className="card card-in streak-strip" style={{ padding: "1rem 1.1rem", marginBottom: "1rem" }}>
      <div className="row" style={{ justifyContent: "space-between", alignItems: "baseline", marginBottom: "0.9rem" }}>
        <div style={{ fontWeight: 700, fontSize: "var(--fs-sm)" }}>Your streak</div>
        <div style={{ fontSize: "var(--fs-sm)", color: "var(--accent)", fontWeight: 700 }}>
          {streak > 0 ? `${streak} day${streak === 1 ? "" : "s"} running` : "Start today"}
        </div>
      </div>
      <div className="streak-row">
        <div className="streak-line" style={{ "--fill": fill } as React.CSSProperties} />
        {days.map((d, i) => {
          const active = activeDays.has(d.key);
          const isToday = i === days.length - 1;
          return (
            <div key={d.key} className={`streak-node ${active ? "active" : ""}`} style={{ "--i": i } as React.CSSProperties}
              title={`${d.date.toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" })}${active ? " — focused" : ""}`}>
              <span className="streak-dot" />
              <span className={`streak-label ${isToday ? "today" : ""}`}>{d.label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── The focus grid — day × time-of-day (ledger row 2b: the primitive must come from the DOMAIN
// OBJECT, not the scaffold) ─────────────────────────────────────────────────────────────────────
// A focus session is not a document in a list — it is MINUTES AT A TIME OF DAY, ON A DAY. Its two
// real axes are day and hour-band, so the primitive it demands is a grid, not the vertical card
// list this app shipped. The list answers "what exists"; the question a deep-work user actually
// has is "WHEN do I focus, and where did I go dark" — which the app already knew (it computes
// hour_clustering.peak_band and distinct_active_days) and then buried in a one-line stat tile.
//
// Sibling check (row 2b q3): Habits uses day-dots and Mood uses daily bars — both DAY-ONLY, one
// axis. Plant Diary's CadenceGrid is three stat tiles, not temporal. A day × band grid is two-axis
// and unused anywhere in the fleet, so this differentiates on the bones, not on paint.
//
// ⚠️ The band boundaries and the local-time conversion below MIRROR src-tauri/src/db.rs::band_of
// EXACTLY (5-11 / 12-16 / 17-21 / else, in LOCAL time). The Rust is the authority — if it ever
// changes, change it here too. Two views of one week disagreeing on which band a session fell in
// reads to the user as a rendering bug, and it would put a different answer next to the
// "Peak time" tile computed from the same sessions.
const BANDS = ["morning", "afternoon", "evening", "night"] as const;
const BAND_SHORT: Record<string, string> = { morning: "morn", afternoon: "aft", evening: "eve", night: "night" };
function bandOfHour(h: number): string {
  if (h >= 5 && h <= 11) return "morning";
  if (h >= 12 && h <= 16) return "afternoon";
  if (h >= 17 && h <= 21) return "evening";
  return "night";
}
function FocusGrid({ sessions }: { sessions: FocusSession[] }) {
  // Last 7 calendar days, oldest first — the same 7-day window the SignalStrip's "Active days /7"
  // and week-over-week tiles use, so the grid and the tiles always describe the same period.
  const days: { key: string; label: string }[] = [];
  const now = new Date();
  for (let i = 6; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() - i);
    days.push({
      key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`,
      label: d.toLocaleDateString(undefined, { weekday: "short" }),
    });
  }
  const cells: Record<string, number> = {};
  let max = 0;
  for (const s of sessions) {
    const dt = new Date(s.started_at);
    if (Number.isNaN(dt.getTime())) continue; // unparseable timestamp — skip, never guess a slot
    const key = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
    if (!days.some((d) => d.key === key)) continue;
    const cell = `${key}|${bandOfHour(dt.getHours())}`;
    cells[cell] = (cells[cell] ?? 0) + s.duration_min;
    if (cells[cell] > max) max = cells[cell];
  }
  const totalMin = Object.values(cells).reduce((a, n) => a + n, 0);

  return (
    <div className="card card-in" style={{ padding: "0.9rem 1rem", marginBottom: "1rem" }}>
      <div className="row" style={{ justifyContent: "space-between", alignItems: "baseline", marginBottom: "0.6rem" }}>
        <div style={{ fontWeight: 700, fontSize: "var(--fs-sm)" }}>When you focused</div>
        <div className="muted" style={{ fontSize: "var(--fs-xs)", color: "var(--ink-2)" }}>last 7 days · {totalMin}m</div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "2.2rem repeat(4, minmax(0, 1fr))", gap: 3, alignItems: "center" }}>
        <div />
        {BANDS.map((b) => (
          <div key={b} className="muted" style={{ fontSize: 10, textAlign: "center", color: "var(--ink-3)" }}>{BAND_SHORT[b]}</div>
        ))}
        {days.map((d) => (
          <Fragment key={d.key}>
            <div className="muted" style={{ fontSize: 10, color: "var(--ink-3)" }}>{d.label}</div>
            {BANDS.map((b) => {
              const v = cells[`${d.key}|${b}`] ?? 0;
              // Shade by share of the busiest cell. A floor of 0.18 keeps a 5-minute session
              // visible instead of rendering as an empty slot — an all-but-invisible cell would
              // read as "I did nothing", which is the opposite of true.
              const alpha = v === 0 ? 0 : 0.18 + 0.82 * (max > 0 ? v / max : 0);
              return (
                <div
                  key={b}
                  title={v > 0 ? `${d.label} ${BAND_SHORT[b]} — ${v} min focused` : `${d.label} ${BAND_SHORT[b]} — nothing logged`}
                  style={{
                    height: 22, borderRadius: 4,
                    background: v === 0 ? "transparent" : `color-mix(in srgb, var(--accent) ${Math.round(alpha * 100)}%, transparent)`,
                    border: v === 0 ? "1px dashed var(--border)" : "1px solid transparent",
                  }}
                />
              );
            })}
          </Fragment>
        ))}
      </div>
    </div>
  );
}

function SessionCard({ s, onChange }: { s: FocusSession; onChange: () => void }) {
  const [confirm, setConfirm] = useState(false);
  async function remove() { setConfirm(false); await store.deleteSession(s.id); onChange(); }
  return (
    <div className="session-card">
      <div className="row" style={{ gap: "0.85rem" }}>
        <div className="duration-badge">{s.duration_min}m</div>
        {/* The photo filed from the chat composer — on-device only, never sent to the model. */}
        {s.photo_ref && (
          <img src={s.photo_ref} alt=""
            style={{ width: 40, height: 40, objectFit: "cover", borderRadius: "var(--radius-sm)", flexShrink: 0 }} />
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600 }}>{fmtDate(s.started_at)}</div>
          <div className="muted" style={{ fontSize: "var(--fs-sm)", color: "var(--ink-2)" }}>{fmtTime(s.started_at)}</div>
        </div>
        <span className={`status-dot ${s.completed_at ? "dot-complete" : "dot-incomplete"}`} title={s.completed_at ? "Completed" : "Stopped early"} />
        <button className="btn btn-ghost" style={{ fontSize: "var(--fs-sm)", padding: "0.3rem 0.5rem", color: "var(--ink-3)" }} title="Delete" onClick={() => setConfirm(true)}>Delete</button>
      </div>
      {s.reflection && <div className="reflection">"{s.reflection}"</div>}
      {confirm && (
        <div className="modal-overlay">
          <div className="modal-card" style={{ width: "100%", maxWidth: 340 }}>
            <div style={{ fontWeight: 700, marginBottom: "0.6rem" }}>Delete this session?</div>
            <p className="muted" style={{ fontSize: "var(--fs-sm)", margin: "0 0 1rem", color: "var(--ink-2)" }}>
              {s.duration_min}m on {fmtDate(s.started_at)}. This can't be undone.
            </p>
            <div className="row" style={{ justifyContent: "flex-end", gap: "0.5rem" }}>
              <button className="btn btn-ghost" onClick={() => setConfirm(false)}>Cancel</button>
              <button className="btn btn-danger" onClick={remove}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// The ONE AI feature — calls the LIVE server brain /api/apps/solo_focus_log/weekly_summary.
// Sends the scoped past-7-days slice and renders the plain-English summary. All billing/
// balance lives in the customer's Oriora account on the website — never in this app.

type InsightState =
  | { kind: "idle" }
  | { kind: "empty" }                                   // no sessions in the last 7 days
  | { kind: "result"; summary: string; signals: WeeklySignals }
  | { kind: "error"; code: BrainErrorCode | "empty_result"; message: string };

const BAND_LABEL: Record<string, string> = {
  morning: "mornings", afternoon: "afternoons", evening: "evenings", night: "late nights", none: "—",
};
const fmtHour = (h: number) => {
  const period = h < 12 ? "am" : "pm";
  const hr = h % 12 === 0 ? 12 : h % 12;
  return `${hr}${period}`;
};
const fmtPct = (frac: number) => `${Math.round(frac * 100)}%`;
const fmtSigned = (n: number) => (n > 0 ? `+${n}` : `${n}`);

// Compact stat tiles for the on-device derived signals. These render the SAME numbers the
// AI narrates, so the user sees the computed pattern (not just raw rows) at a glance.
function SignalStrip({ signals: s }: { signals: WeeklySignals }) {
  const wow = s.week_over_week;
  const wowText =
    wow.minutes_pct_change === null
      ? `${fmtSigned(wow.minutes_delta)}m vs prior wk`
      : `${fmtSigned(wow.minutes_delta)}m (${wow.minutes_pct_change > 0 ? "+" : ""}${Math.round(wow.minutes_pct_change)}%)`;
  const trend = s.duration_trend.slope_min_per_session;
  const trendText =
    s.duration_trend.n < 2
      ? "—"
      : Math.abs(trend) < 0.05
      ? "flat"
      : `${trend > 0 ? "↑" : "↓"} ${Math.abs(trend).toFixed(1)}m/session`;
  const peak =
    s.hour_clustering.peak_hour === null
      ? BAND_LABEL[s.hour_clustering.peak_band] ?? "—"
      : `${BAND_LABEL[s.hour_clustering.peak_band] ?? "—"} · ${fmtHour(s.hour_clustering.peak_hour)}`;

  const tiles: { label: string; value: string; title?: string }[] = [
    { label: "Completed", value: fmtPct(s.completion_rate), title: `${s.completed_total} of ${s.sessions_total} sessions ran to completion` },
    { label: "Peak time", value: peak, title: `Most sessions start in the ${BAND_LABEL[s.hour_clustering.peak_band] ?? "—"} band` },
    { label: "Week vs prior", value: wowText, title: `This week ${wow.this_week.total_minutes}m · prior week ${wow.prior_week.total_minutes}m` },
    { label: "Length trend", value: trendText, title: "Least-squares slope of session length across the week" },
    { label: "Reflections", value: `${s.reflections.with_reflection}/${s.reflections.sessions_total}`, title: `Avg ${Math.round(s.reflections.avg_length_chars)} chars (text stored on this device unless you back it up)` },
    { label: "Active days", value: `${s.distinct_active_days}/7`, title: "Calendar days with at least one session" },
  ];
  return (
    <div className="signal-strip">
      {tiles.map((t) => (
        <div key={t.label} className="signal-tile" title={t.title}>
          <div className="signal-value">{t.value}</div>
          <div className="signal-label">{t.label}</div>
        </div>
      ))}
    </div>
  );
}

// The pre-ranked observational facts the on-device slice already computed (full sentences,
// figure+unit baked in). Shown as a compact "what your numbers show" list under the AI
// narration — each tinted by its NEUTRAL tone (a reading hint only, never a verdict).
const TONE_DOT: Record<FactTone, string> = {
  improving: "var(--accent)",
  rising: "var(--accent)",
  steady: "var(--ink-3)",
  notable: "var(--amber)",
  neutral: "var(--ink-3)",
};
function HeadlineFacts({ facts }: { facts: HeadlineFact[] }) {
  const top = facts.slice(0, 4);
  if (top.length === 0) return null;
  return (
    <div style={{ marginTop: "0.9rem", borderTop: "1px solid var(--border)", paddingTop: "0.8rem" }}>
      <div className="micro" style={{ marginBottom: "0.55rem", color: "var(--ink-3)" }}>What your numbers show</div>
      <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "0.5rem" }}>
        {top.map((f) => (
          <li key={f.rank} className="row" style={{ gap: "0.55rem", alignItems: "flex-start", flexWrap: "nowrap" }}>
            <span aria-hidden style={{ width: 6, height: 6, borderRadius: "50%", background: TONE_DOT[f.tone] ?? "var(--ink-3)", flexShrink: 0, marginTop: 7 }} />
            <span className="tnum" style={{ fontSize: "var(--fs-sm)", color: "var(--ink-2)", lineHeight: 1.5 }}>{f.text}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function WeeklyInsight({ offline }: { offline: boolean }) {
  const [busy, setBusy] = useState(false);
  const [state, setState] = useState<InsightState>({ kind: "idle" });

  async function run() {
    if (offline) { setState({ kind: "error", code: "unavailable", message: "You're offline — reconnect to summarize your week." }); return; }
    setBusy(true); setState({ kind: "idle" });
    try {
      // Compute the derived-signal aggregate on-device (Rust). Only THIS leaves the Mac —
      // no raw session rows, no reflection text. Then the brain narrates the numbers.
      const signals = await store.weeklySignals(7);
      if (signals.sessions_total === 0) { setState({ kind: "empty" }); return; }
      const r = await weeklySummary(signals);
      if (!r?.summary) { setState({ kind: "error", code: "empty_result", message: "The AI returned an empty summary — please try again." }); return; }
      setState({ kind: "result", summary: r.summary, signals });
    } catch (e) {
      if (e instanceof BrainError) setState({ kind: "error", code: e.code, message: e.message });
      else setState({ kind: "error", code: "unavailable", message: "Weekly summary failed — please try again." });
    } finally {
      setBusy(false);
    }
  }

  const hasBody = state.kind !== "idle";
  const outOfFunds = state.kind === "error" && state.code === "insufficient_funds";

  return (
    <div className="card card-in" style={{ padding: "1rem 1.25rem", marginBottom: "1.25rem", borderLeft: "3px solid var(--accent)" }}>
      <div className="row" style={{ justifyContent: "space-between", marginBottom: hasBody ? "0.75rem" : 0, gap: "0.75rem" }}>
        <div className="row" style={{ gap: "0.4rem", fontWeight: 600, fontSize: "var(--fs-md)", minWidth: 0 }}>
          <span style={{ color: "var(--accent)" }}>✦</span> Weekly focus summary
        </div>
        <button className="btn btn-primary" style={{ fontSize: "var(--fs-sm)", flexShrink: 0 }} disabled={busy || offline} onClick={run}
          title={offline ? "Unavailable while offline" : undefined}>
          {busy ? "Summarizing…" : state.kind === "result" ? "Refresh" : "Summarize my week"}
        </button>
      </div>

      {/* Loading — the AI is running. */}
      {busy && (
        <div className="row muted" style={{ gap: "0.5rem", fontSize: "var(--fs-sm)", color: "var(--ink-2)" }}>
          <span className="spinner" /> Reading your last 7 days of sessions…
        </div>
      )}

      {/* Success — the derived-signal stat strip, the AI's observation of those numbers,
          then the observational guardrail label. */}
      {!busy && state.kind === "result" && (
        <>
          <SignalStrip signals={state.signals} />
          <p style={{ fontSize: "var(--fs-md)", color: "var(--ink-1)", lineHeight: 1.6, margin: "0.9rem 0 0" }}>{state.summary}</p>
          <div style={{ marginTop: 8 }}><OrioraBadge /></div>
          <HeadlineFacts facts={state.signals.headline_facts} />
          <div className="muted" style={{ fontSize: "var(--fs-xs)", color: "var(--ink-3)", marginTop: "0.65rem", fontStyle: "italic" }}>
            Observational only — patterns in your own numbers, not advice.
          </div>
          <div className="row" style={{ marginTop: "0.85rem", paddingTop: "0.75rem", borderTop: "1px solid var(--border)", fontSize: "var(--fs-xs)", color: "var(--ink-3)" }}>
            <span>{state.signals.sessions_total} session{state.signals.sessions_total !== 1 ? "s" : ""} · last 7 days</span>
          </div>
        </>
      )}

      {/* Empty — no sessions to summarize yet. */}
      {!busy && state.kind === "empty" && (
        <p style={{ fontSize: "var(--fs-sm)", color: "var(--ink-2)", margin: 0 }}>
          No sessions in the past 7 days yet. Run a focus session on the Timer tab, then summarize.
        </p>
      )}

      {/* Error — out of funds points to the Oriora account on the website (no amounts); others show the message. */}
      {!busy && state.kind === "error" && (
        <div style={{ fontSize: "var(--fs-sm)" }}>
          <p style={{ color: "var(--danger)", margin: 0 }}>{state.message}</p>
          {outOfFunds && (
            <button className="btn" style={{ marginTop: "0.6rem", fontSize: "var(--fs-sm)" }}
              onClick={() => void invoke("open_external", { url: "https://orioralabs.com/account" })}>
              Add funds in your Oriora account
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ── Settings ────────────────────────────────────────────────────────────────────
function SettingsView({ email, settings, onChange }: { email: string | null; settings: Settings; onChange: () => void }) {
  async function pick(minutes: number) { await store.setDefaultDuration(minutes); onChange(); }
  return (
    <div>
      <h1 style={{ fontSize: "var(--fs-heading)", fontWeight: 700, marginBottom: "1.5rem" }}>Settings</h1>

      <div style={{ marginBottom: "2rem" }}>
        <div className="micro" style={{ marginBottom: "0.75rem" }}>Account &amp; balance</div>
        <div className="card" style={{ padding: "1rem" }}>
          <div className="muted" style={{ fontSize: "var(--fs-xs)", marginBottom: 4 }}>Signed in as</div>
          <div style={{ fontSize: "var(--fs-lg)", fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{email ?? "—"}</div>
          {/* Add funds shown INLINE here (not only inside the account popover) so the In-App Purchases
              are always visible on the obvious Settings screen — Apple 2.1(b) rejected v1.0 because the
              reviewer could not locate the IAPs when they lived only in the account avatar's popover
              (iPad Air 11" M3, 2026-09-04). WalletTopUp renders the "Add funds" tiles + balance links. */}
          <WalletTopUp />
          {/* Full account menu (manage + account deletion for Apple 5.1.1(v)) stays available here too. */}
          <div className="row" style={{ justifyContent: "flex-start", alignItems: "center", gap: 8, marginTop: 14, paddingTop: 14, borderTop: "1px solid var(--border)" }}>
            <AccountMenu email={email} appSlug="solo-focus-log" appName="Oriora Focus" />
            <span className="muted" style={{ fontSize: "var(--fs-sm)" }}>Manage account &amp; sign out</span>
          </div>
        </div>
      </div>

      <div style={{ marginBottom: "2rem" }}>
        <div className="micro" style={{ marginBottom: "0.75rem" }}>Default session duration</div>
        <div className="row" style={{ gap: "0.75rem" }}>
          {DURATIONS.map((d) => (
            <button key={d} className={`chip ${settings.default_duration_min === d ? "active" : ""}`} style={{ flex: 1 }} onClick={() => pick(d)}>{d} min</button>
          ))}
        </div>
      </div>

      <div style={{ marginBottom: "2rem" }}>
        <div className="micro" style={{ marginBottom: "0.75rem" }}>Privacy &amp; AI</div>
        <div className="card" style={{ padding: "1rem", fontSize: "var(--fs-sm)", color: "var(--ink-2)", lineHeight: 1.55 }}>
          <p style={{ margin: "0 0 0.75rem" }}>
            Your focus sessions and reflections are stored on this device unless you back it up. When you request a weekly
            summary, only computed signals — counts, completion rate, hour-of-day clustering, week-over-week
            totals, a duration trend, active-day streaks, session-length spread, a weekday rhythm, reflection
            counts and lengths, and short ready-made fact sentences built from those numbers — are sent to
            the Oriora AI brain. Your reflection text and individual session rows aren't sent to the AI,
            and nothing is stored server-side.
          </p>
          <p style={{ margin: "0 0 0.75rem" }}>
            The weekly summary runs on Oriora, which routes the request to third-party AI providers
            (Anthropic, OpenAI, Google, and others) to narrate those numbers. It is observational only —
            never advice.
          </p>
          <button className="btn btn-ghost" style={{ fontSize: "var(--fs-sm)", padding: "0.25rem 0.5rem", marginLeft: "-0.5rem" }}
            onClick={() => void invoke("open_external", { url: "https://orioralabs.com/about/privacy" })}>
            Privacy Policy →
          </button>
        </div>
      </div>

    </div>
  );
}
